import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import client from '../lib/client';
import { useOfficeHost } from '../lib/useOfficeHost';
export const useOAuthIntegrations = () => {
    const { user } = useAuth();
    const { isOutlook, isPowerPoint, hostType, isInitialized: hostInitialized } = useOfficeHost();
    const [integrations, setIntegrations] = useState([]);
    const [_userCredentials, setUserCredentials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const fetchIntegrations = async () => {
        if (!user?.access_token)
            return;
        try {
            setLoading(true);
            setError(null);
            // Get the user ID from the Profile endpoint
            let actualUserId = '';
            try {
                const profileResponse = await client.get(`/v1/Profile`, {
                    headers: {
                        Authorization: `Bearer ${user.access_token}`,
                    },
                });
                actualUserId = profileResponse.data.userId || profileResponse.data.id;
            }
            catch (profileError) {
                console.warn('Could not fetch profile, falling back to token sub:', profileError);
                actualUserId = user.profile?.sub || '';
            }
            let pipelineId = '';
            // Select deployment endpoint based on Office host type
            let deploymentEndpoint = '/v2/Deployments/AddinChat';
            if (isOutlook) {
                deploymentEndpoint = '/v2/Deployments/AddinOutlookEmailAssistant';
            }
            else if (isPowerPoint) {
                deploymentEndpoint = '/v2/Deployments/AddinPowerPointChat';
            }
            else if (hostType === Office.HostType.Excel) {
                deploymentEndpoint = '/v2/Deployments/AddinExcelChat';
            }
            // Next, get the deployment info to extract the pipeline ID
            try {
                const deploymentResponse = await client.get(deploymentEndpoint, {
                    headers: {
                        Authorization: `Bearer ${user.access_token}`,
                    },
                });
                const deployment = deploymentResponse.data;
                pipelineId = deployment.pipelineId;
            }
            catch (deploymentError) {
                console.warn('Could not fetch deployment info:', deploymentError);
            }
            // Now fetch the actual integrations for this pipeline - this is the key!
            let availableIntegrations = [];
            if (pipelineId) {
                try {
                    // Try pipeline-specific integrations first (following airia-chat-web pattern)
                    const pipelineIntegrationsResponse = await client.get(`/v1/PipelinesConfig/${pipelineId}/integrations`, {
                        headers: {
                            Authorization: `Bearer ${user.access_token}`,
                        },
                    });
                    const integrationsData = pipelineIntegrationsResponse.data;
                    // Extract integrations from the response (could be in different formats)
                    if (integrationsData.integrationsMap) {
                        availableIntegrations = Object.values(integrationsData.integrationsMap);
                    }
                    else if (integrationsData.items) {
                        availableIntegrations = integrationsData.items;
                    }
                    else if (Array.isArray(integrationsData)) {
                        availableIntegrations = integrationsData;
                    }
                }
                catch (pipelineError) {
                    console.warn('Could not fetch pipeline integrations:', pipelineError);
                    // Fallback: try project-level integrations
                    try {
                        const projectIntegrationsResponse = await client.get(`/v1/Project/integrations`, {
                            headers: {
                                Authorization: `Bearer ${user.access_token}`,
                            },
                        });
                        availableIntegrations = projectIntegrationsResponse.data.items || [];
                    }
                    catch (projectError) {
                        console.warn('Could not fetch project integrations:', projectError);
                    }
                }
            }
            // Filter integrations that support OAuth (following airia-chat-web pattern)
            const supportsOAuthUserFlow = (integration) => {
                return ((integration?.credentialSettings?.platformSupportedOAuthTypes?.length ?? 0) > 0);
            };
            const oauthIntegrations = availableIntegrations
                .filter((integration) => supportsOAuthUserFlow(integration))
                .map((integration) => {
                const availableTypes = integration.credentialSettings?.availableCredentialTypes || [];
                return {
                    id: integration.toolId || integration.id || 'unknown',
                    name: integration.name,
                    provider: integration.provider,
                    isConnected: false,
                    isAvailable: true,
                    credentialType: availableTypes[0], // Use first available type
                    integration,
                };
            });
            // Try to fetch user's existing OAuth credentials to check connection status
            try {
                const credentialsResponse = await client.get(`/v1/Credentials/personal/${actualUserId}`, {
                    headers: {
                        Authorization: `Bearer ${user.access_token}`,
                    },
                });
                const credentials = credentialsResponse.data.items || [];
                setUserCredentials(credentials);
                // Update connection status based on existing credentials
                oauthIntegrations.forEach((integration) => {
                    const credentialTypes = integration.integration?.credentialSettings?.availableCredentialTypes || [];
                    // Check credentials directly against the fetched data, not the state
                    const hasCredentials = credentials.some((credential) => credentialTypes.includes(credential.type) &&
                        credential.name &&
                        credential.name.startsWith('office-'));
                    integration.isConnected = hasCredentials;
                });
            }
            catch (credError) {
                console.warn('Could not fetch user credentials:', credError);
                // Continue with default connection status (false)
            }
            setIntegrations(oauthIntegrations);
        }
        catch (err) {
            console.error('Failed to fetch OAuth integrations:', err);
            setError('Failed to load OAuth integrations');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (hostInitialized) {
            fetchIntegrations();
        }
    }, [user?.access_token, hostInitialized, isOutlook, isPowerPoint, hostType]);
    const refreshIntegrations = () => {
        fetchIntegrations();
    };
    return {
        integrations,
        loading,
        error,
        refreshIntegrations,
        hasOAuthRequirements: integrations.some((integration) => !integration.isConnected),
    };
};
